import { PROFANITY_ERROR_MESSAGE, isProfaneText } from "./profanityPolicy.js";

export const COMMENT_MAX_LENGTH = 420;
export const COMMENT_CREATE_COOLDOWN_SECONDS = 30;
export const COMMENT_MARKET_DAILY_LIMIT = 10;
export const COMMENT_USER_DAILY_LIMIT = 50;
export const COMMENT_IP_HOURLY_LIMIT = 120;
export const COMMENT_USER_HOURLY_LIMIT = 60;
export const COMMENT_LIKE_HOURLY_LIMIT = 120;
export const COMMENT_REPORT_HOURLY_LIMIT = 20;
export const COMMENT_HIDE_HOURLY_LIMIT = 60;
export const COMMENT_URL_ERROR = "Comments cannot include links.";
export const COMMENT_PROFANITY_ERROR = PROFANITY_ERROR_MESSAGE;
export const COMMENT_REPORT_REASONS = ["spam", "harassment", "hate", "manipulation", "personal_information", "other"];
export const COMMENT_SORTS = ["top", "newest"];

const urlLikePattern =
  /(?:https?:\/\/|www\.|[a-z0-9-]+\.[a-z]{2,}|[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}|\[[^\]]+\]\([^)]+\)|\b[a-z0-9-]+\s+dot\s+[a-z]{2,}\b)/i;
const solanaAddressPattern = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

export function getMarketCommentConfig(env = {}) {
  return {
    maxLength: intFromEnv(env.PM_COMMENT_MAX_LENGTH, COMMENT_MAX_LENGTH, 1, COMMENT_MAX_LENGTH),
    createCooldownSeconds: intFromEnv(
      env.PM_COMMENT_CREATE_COOLDOWN_SECONDS,
      COMMENT_CREATE_COOLDOWN_SECONDS,
      1,
      3600
    ),
    marketDailyLimit: intFromEnv(env.PM_COMMENT_MARKET_DAILY_LIMIT, COMMENT_MARKET_DAILY_LIMIT, 1, 500),
    userDailyLimit: intFromEnv(env.PM_COMMENT_USER_DAILY_LIMIT, COMMENT_USER_DAILY_LIMIT, 1, 2000),
    ipHourlyLimit: intFromEnv(env.PM_COMMENT_IP_HOURLY_LIMIT, COMMENT_IP_HOURLY_LIMIT, 1, 10000),
    userHourlyLimit: intFromEnv(env.PM_COMMENT_USER_HOURLY_LIMIT, COMMENT_USER_HOURLY_LIMIT, 1, 5000),
    likeHourlyLimit: intFromEnv(env.PM_COMMENT_LIKE_HOURLY_LIMIT, COMMENT_LIKE_HOURLY_LIMIT, 1, 10000),
    reportHourlyLimit: intFromEnv(env.PM_COMMENT_REPORT_HOURLY_LIMIT, COMMENT_REPORT_HOURLY_LIMIT, 1, 1000),
    hideHourlyLimit: intFromEnv(env.PM_COMMENT_HIDE_HOURLY_LIMIT, COMMENT_HIDE_HOURLY_LIMIT, 1, 1000)
  };
}

function intFromEnv(value, fallback, min, max) {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

export function normalizeCommentBody(body) {
  return String(body || "")
    .replace(/\s+/g, " ")
    .trim();
}

export function sanitizeCommentInput(input, { maxLength = COMMENT_MAX_LENGTH } = {}) {
  const body = normalizeCommentBody(input?.body);
  if (!body) return { value: null, error: "Comment cannot be empty." };
  if (body.length > maxLength) return { value: null, error: `Comment must be ${maxLength} characters or fewer.` };
  if (urlLikePattern.test(body)) return { value: null, error: COMMENT_URL_ERROR };
  if (isProfaneText(body)) return { value: null, error: COMMENT_PROFANITY_ERROR };
  return { value: { body }, error: null };
}

export function isCommentRateLimitMessage(message) {
  return (
    /^Please wait \d+ seconds before posting another comment\.$/.test(String(message || "")) ||
    message === "Daily comment limit reached for this market." ||
    message === "Daily comment limit reached."
  );
}

export function normalizeCommentSort(sort) {
  return COMMENT_SORTS.includes(String(sort || "").toLowerCase()) ? String(sort).toLowerCase() : "top";
}

export function sanitizeReportInput(input) {
  const reason = String(input?.reason || "").trim();
  const details = normalizeCommentBody(input?.details || "").slice(0, 500);
  if (!COMMENT_REPORT_REASONS.includes(reason)) return { value: null, error: "Invalid report reason." };
  return { value: { reason, details }, error: null };
}

export function buildCommentPermalink({ origin, marketId, commentId, affiliateId = "" } = {}) {
  const safeOrigin = String(origin ?? "").trim();
  const safeMarketId = String(marketId ?? "").trim();
  const safeCommentId = String(commentId ?? "").trim();
  if (!safeOrigin || !safeMarketId || !safeCommentId) return "#";

  let url;
  try {
    url = new URL(`/market/${encodeURIComponent(safeMarketId)}`, safeOrigin);
  } catch {
    return "#";
  }

  if (!["http:", "https:"].includes(url.protocol)) return "#";

  url.searchParams.set("comment", safeCommentId);
  const safeAffiliateId = String(affiliateId ?? "").trim();
  if (solanaAddressPattern.test(safeAffiliateId)) url.searchParams.set("aff", safeAffiliateId);
  url.hash = `comment-${safeCommentId}`;
  return url.toString();
}

export function buildCommentShareUrl({ origin, marketId, marketTitle, commentId, commentBody, affiliateId = "" } = {}) {
  const permalink = buildCommentPermalink({ origin, marketId, commentId, affiliateId });
  if (permalink === "#") return "#";

  const intent = new URL("https://twitter.com/intent/tweet");
  const title = truncateText(marketTitle, 90);
  const excerpt = truncateText(commentBody, 140);
  intent.searchParams.set("text", `A take from the RaizIQ board:\n\n${title}\n\n${excerpt}`);
  intent.searchParams.set("url", permalink);
  return intent.toString();
}

export function truncateText(value, maxLength) {
  const limit = Math.max(0, Math.floor(Number(maxLength) || 0));
  const text = normalizeCommentBody(value);
  if (text.length <= limit) return text;
  if (limit <= 3) return ".".repeat(limit);
  return `${text.slice(0, limit - 3).trimEnd()}...`;
}
